/**
 * Input. See ARCHITECTURE.md §4.3.
 *
 * Produces an analog InputState from keyboard, gamepad, mouse, or a deterministic tape.
 * Crazy Taxi feel note: digital keys must ramp to full lock fast enough to feel instant
 * but not so fast the truck snaps. Ramp constants live here because they are input feel,
 * not vehicle physics.
 */

const STEER_ATTACK = 7.5;    // 1/s toward target when pressing
const STEER_RELEASE = 12.0;  // 1/s toward zero when released — release is snappier than attack
const PEDAL_ATTACK = 14.0;
const PEDAL_RELEASE = 18.0;
const DEADZONE = 0.14;

function approach(cur, target, rate, dt) {
  const d = target - cur;
  const step = rate * dt;
  if (Math.abs(d) <= step) return target;
  return cur + Math.sign(d) * step;
}

function applyDeadzone(v) {
  const a = Math.abs(v);
  if (a < DEADZONE) return 0;
  return Math.sign(v) * ((a - DEADZONE) / (1 - DEADZONE));
}

export class Input {
  constructor(domElement) {
    this.dom = domElement || window;

    // Public state — read by everyone, written only here.
    this.steer = 0;
    this.throttle = 0;
    this.brake = 0;
    this.handbrake = 0;
    this.trigger = 0;
    this.nozzle = { x: 0, y: 0 };
    this.gearToggle = false;
    this.horn = false;
    this.pause = false;
    this.anyPressed = false;

    /** Raw target values before smoothing — useful for debug HUD. */
    this.raw = { steer: 0, throttle: 0, brake: 0, trigger: 0 };

    this._keys = new Set();
    this._edge = new Set();      // keys pressed this step
    this._prevEdge = new Set();
    this._mouseDown = 0;
    this._pointerLocked = false;
    this._mouseDx = 0;
    this._mouseDy = 0;
    this._enabled = true;

    // Deterministic tape playback
    this._tape = null;
    this._tapeIndex = 0;
    this._tapeFrame = 0;

    this._bound = false;
  }

  bind() {
    if (this._bound) return;
    this._bound = true;
    const onKey = (down) => (e) => {
      if (e.repeat) return;
      const code = e.code;
      if (down) {
        if (!this._keys.has(code)) this._edge.add(code);
        this._keys.add(code);
      } else {
        this._keys.delete(code);
      }
      // Prevent page scroll on the driving keys.
      if (/^(Arrow|Space|Key[WASDER]|Shift)/.test(code)) e.preventDefault();
    };
    this._onKeyDown = onKey(true);
    this._onKeyUp = onKey(false);
    window.addEventListener('keydown', this._onKeyDown, { passive: false });
    window.addEventListener('keyup', this._onKeyUp, { passive: false });

    this._onBlur = () => { this._keys.clear(); this._mouseDown = 0; };
    window.addEventListener('blur', this._onBlur);

    this._onMouseDown = (e) => { this._mouseDown |= (1 << e.button); };
    this._onMouseUp = (e) => { this._mouseDown &= ~(1 << e.button); };
    this._onMouseMove = (e) => {
      if (this._pointerLocked) { this._mouseDx += e.movementX; this._mouseDy += e.movementY; }
    };
    const el = this.dom === window ? window : this.dom;
    el.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('mousemove', this._onMouseMove);
    this._onContext = (e) => e.preventDefault();
    el.addEventListener('contextmenu', this._onContext);
    this._onLockChange = () => { this._pointerLocked = !!document.pointerLockElement; };
    document.addEventListener('pointerlockchange', this._onLockChange);
  }

  dispose() {
    if (!this._bound) return;
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('pointerlockchange', this._onLockChange);
    const el = this.dom === window ? window : this.dom;
    el.removeEventListener('mousedown', this._onMouseDown);
    el.removeEventListener('contextmenu', this._onContext);
    this._bound = false;
  }

  /**
   * Feed a closed-loop DRIVER instead of a fixed tape.
   *
   * A fixed input tape cannot play a procedurally generated city: it drives blind, hits
   * walls, and never reaches a pothole — the round-2 playtest logged 9 collisions, measured
   * "braking" from 5.3 km/h, and completed 0 repairs, so every feel metric downstream was
   * noise. A driver is still fully deterministic (it is a pure function of simulation
   * state) but it actually plays the game.
   *
   * `fn()` takes no arguments — it reads game state itself — and returns raw input targets
   * `{steer, throttle, brake, trigger, handbrake, nozzleX, nozzleY}`. The same smoothing
   * that applies to a human applies here, so measured latency stays meaningful.
   */
  setDriver(fn) {
    this._driver = typeof fn === 'function' ? fn : null;
    if (this._driver) this._tape = null;
  }

  get driven() { return !!this._driver; }

  /** Feed a deterministic tape. See ARCHITECTURE.md §7.2. Pass null to release. */
  setScript(tape) {
    this._driver = null;
    this._tape = tape && tape.length ? tape.map((f) => ({ frames: f.frames | 0, ...f })) : null;
    this._tapeIndex = 0;
    this._tapeFrame = 0;
    if (this._tape) { this.steer = 0; this.throttle = 0; this.brake = 0; this.trigger = 0; }
  }

  get scripted() { return this._tape !== null; }
  get scriptFinished() { return this._tape !== null && this._tapeIndex >= this._tape.length; }

  /** Called once per rendered frame before stepping. */
  poll() {
    if (this._tape) return;
    this._pollGamepad();
  }

  _pollGamepad() {
    if (!navigator.getGamepads) return;
    const pads = navigator.getGamepads();
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (!p || !p.connected) continue;
      this._pad = p;
      return;
    }
    this._pad = null;
  }

  /**
   * Called once per FIXED step. Resolves raw targets then smooths.
   * dt is always SIM_DT.
   */
  beginStep(dt) {
    if (this._driver) { this._stepDriver(dt); return; }
    if (this._tape) { this._stepTape(dt); return; }
    if (!this._enabled) { this._decayAll(dt); return; }

    const k = this._keys;
    let steerT = 0, thrT = 0, brkT = 0, hbT = 0, trigT = 0;

    if (k.has('KeyA') || k.has('ArrowLeft')) steerT -= 1;
    if (k.has('KeyD') || k.has('ArrowRight')) steerT += 1;
    // Physical pedal axes, not final vehicle actuators. Vehicle keeps W/Up as accelerator and
    // S/Down as brake in D, then swaps their drive/brake roles in R. This is what lets one held
    // S press brake to a stop, select R, and continue backward without core knowing the gear.
    if (k.has('KeyW') || k.has('ArrowUp')) thrT = 1;
    if (k.has('KeyS') || k.has('ArrowDown')) brkT = 1;
    if (k.has('Space')) hbT = 1;
    if (k.has('KeyE') || k.has('ShiftLeft') || k.has('ShiftRight')) trigT = 1;
    if (this._mouseDown & 1) trigT = 1;

    const pad = this._pad;
    if (pad) {
      const ax = applyDeadzone(pad.axes[0] || 0);
      if (Math.abs(ax) > Math.abs(steerT)) steerT = ax;
      const rt = pad.buttons[7] ? pad.buttons[7].value : 0;
      const lt = pad.buttons[6] ? pad.buttons[6].value : 0;
      if (rt > thrT) thrT = rt;
      if (lt > brkT) brkT = lt;
      if (pad.buttons[0] && pad.buttons[0].pressed) hbT = 1;
      if (pad.buttons[2] && pad.buttons[2].pressed) trigT = 1;
      const rx = applyDeadzone(pad.axes[2] || 0);
      const ry = applyDeadzone(pad.axes[3] || 0);
      if (rx || ry) { this.nozzle.x = rx; this.nozzle.y = ry; }
    }

    this.raw.steer = steerT; this.raw.throttle = thrT;
    this.raw.brake = brkT; this.raw.trigger = trigT;

    this.steer = approach(this.steer, steerT, steerT === 0 ? STEER_RELEASE : STEER_ATTACK, dt);
    this.throttle = approach(this.throttle, thrT, thrT === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
    this.brake = approach(this.brake, brkT, brkT === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
    this.handbrake = hbT;
    this.trigger = approach(this.trigger, trigT, trigT === 0 ? 22 : 26, dt);

    // Mouse-aim nozzle, decays to centre.
    if (this._pointerLocked && (this._mouseDx || this._mouseDy)) {
      this.nozzle.x = Math.max(-1, Math.min(1, this.nozzle.x + this._mouseDx * 0.0035));
      this.nozzle.y = Math.max(-1, Math.min(1, this.nozzle.y + this._mouseDy * 0.0035));
    }
    this._mouseDx = 0; this._mouseDy = 0;
    if (trigT === 0) {
      this.nozzle.x = approach(this.nozzle.x, 0, 3, dt);
      this.nozzle.y = approach(this.nozzle.y, 0, 3, dt);
    }

    this.gearToggle = this._edge.has('KeyR') || (pad && pad.buttons[1] && pad.buttons[1].pressed && !this._padBWas);
    this._padBWas = !!(pad && pad.buttons[1] && pad.buttons[1].pressed);
    this.horn = this._edge.has('KeyH');
    this.pause = this._edge.has('Escape') || this._edge.has('KeyP');
    this.anyPressed = k.size > 0 || this._mouseDown !== 0;

    this._prevEdge = this._edge;
    this._edge = new Set();
  }

  _stepTape(dt) {
    const tape = this._tape;
    if (this._tapeIndex >= tape.length) { this._decayAll(dt); return; }
    const f = tape[this._tapeIndex];
    const g = (k, d = 0) => (f[k] === undefined ? d : f[k]);

    // Tapes may be authored as raw targets (default) or as already-smoothed absolutes.
    if (f.absolute) {
      this.steer = g('steer'); this.throttle = g('throttle'); this.brake = g('brake');
      this.trigger = g('trigger'); this.handbrake = g('handbrake');
    } else {
      this.raw.steer = g('steer'); this.raw.throttle = g('throttle');
      this.raw.brake = g('brake'); this.raw.trigger = g('trigger');
      this.steer = approach(this.steer, g('steer'), g('steer') === 0 ? STEER_RELEASE : STEER_ATTACK, dt);
      this.throttle = approach(this.throttle, g('throttle'), g('throttle') === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
      this.brake = approach(this.brake, g('brake'), g('brake') === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
      this.handbrake = g('handbrake');
      this.trigger = approach(this.trigger, g('trigger'), g('trigger') === 0 ? 22 : 26, dt);
    }
    this.nozzle.x = g('nozzleX', 0);
    this.nozzle.y = g('nozzleY', 0);
    this.gearToggle = this._tapeFrame === 0 && !!f.gearToggle;
    this.horn = this._tapeFrame === 0 && !!f.horn;
    this.pause = false;
    this.anyPressed = true;

    this._tapeFrame++;
    if (this._tapeFrame >= f.frames) { this._tapeFrame = 0; this._tapeIndex++; }
  }

  /** Closed-loop driver. Same smoothing path as a human so latency stays comparable. */
  _stepDriver(dt) {
    let f;
    try { f = this._driver() || {}; } catch (e) { console.error('[input.driver]', e); f = {}; }
    const g = (k, d = 0) => (typeof f[k] === 'number' ? Math.max(-1, Math.min(1, f[k])) : d);

    const steerT = g('steer'), thrT = g('throttle'), brkT = g('brake'), trigT = g('trigger');
    this.raw.steer = steerT; this.raw.throttle = thrT;
    this.raw.brake = brkT; this.raw.trigger = trigT;

    this.steer = approach(this.steer, steerT, steerT === 0 ? STEER_RELEASE : STEER_ATTACK, dt);
    this.throttle = approach(this.throttle, thrT, thrT === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
    this.brake = approach(this.brake, brkT, brkT === 0 ? PEDAL_RELEASE : PEDAL_ATTACK, dt);
    this.trigger = approach(this.trigger, trigT, trigT === 0 ? 22 : 26, dt);
    this.handbrake = g('handbrake');
    this.nozzle.x = g('nozzleX'); this.nozzle.y = g('nozzleY');
    this.gearToggle = !!f.gearToggle;
    this.horn = !!f.horn;
    this.pause = false;
    this.anyPressed = true;
  }

  _decayAll(dt) {
    this.steer = approach(this.steer, 0, STEER_RELEASE, dt);
    this.throttle = approach(this.throttle, 0, PEDAL_RELEASE, dt);
    this.brake = approach(this.brake, 0, PEDAL_RELEASE, dt);
    this.trigger = approach(this.trigger, 0, 22, dt);
    this.handbrake = 0;
    this.gearToggle = false; this.horn = false; this.pause = false;
    this.anyPressed = false;
  }

  setEnabled(v) { this._enabled = v; if (!v) this._keys.clear(); }
}
